import { useCallback } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { CheckCircle2, Clock, PackageSearch, Truck, Undo2 } from 'lucide-react'

import type { VtpShipmentStatusGroup } from '@/lib/api/vtp-shipments-api'

import { VtpShipmentsTable } from '@/components/carrier/vtp-shipments-table'
import { defineListFilters } from '@/components/common/filters/core'
import { selectFilter } from '@/components/common/filters/select-filter'
import { textFilter } from '@/components/common/filters/text-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { KpiCard } from '@/components/sections/kpi-cards'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { useClampPage } from '@/hooks/use-clamp-page'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { useVtpShipmentListTracking } from '@/hooks/use-vtp-shipment-tracking'
import {
    useVtpShipmentSummary,
    useVtpShipments,
} from '@/hooks/use-vtp-shipments-query'
import { m } from '@/lib/paraglide/messages'

type StatusFilter = VtpShipmentStatusGroup | 'all'

const FILTERS = defineListFilters({
    q: textFilter({
        label: m.vtp_shipments_search_label,
        placeholder: m.vtp_shipments_search_placeholder,
    }),
    status: selectFilter<StatusFilter>({
        label: m.vtp_shipments_status_label,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.vtp_shipments_status_all() },
            {
                value: 'awaiting_pickup',
                label: m.vtp_shipments_status_awaiting_pickup(),
            },
            {
                value: 'in_delivery',
                label: m.vtp_shipments_status_in_delivery(),
            },
            { value: 'overdue', label: m.vtp_shipments_status_overdue() },
            { value: 'delivered', label: m.vtp_shipments_status_delivered() },
            { value: 'returning', label: m.vtp_shipments_status_returning() },
            { value: 'cancelled', label: m.vtp_shipments_status_cancelled() },
        ],
    }),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/logistics/vtp-shipments/',
)({
    component: VtpShipmentsPage,
    validateSearch: FILTERS.validateSearch,
})

const PER_PAGE = 20

function VtpShipmentsPage() {
    const { hasPermission } = useAuth()
    const navigate = useNavigate({ from: '/logistics/vtp-shipments/' })
    const { values, barProps, page, setPage } = useListFilters({
        def: FILTERS,
        search: Route.useSearch(),
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 3,
    })
    const {
        data,
        isLoading,
        isError,
        isFetching,
        refetch,
        status,
        isPlaceholderData,
    } = useVtpShipments({
        q: values.q || undefined,
        status: values.status === 'all' ? undefined : values.status,
        page,
        per_page: PER_PAGE,
    })
    const summary = useVtpShipmentSummary().data
    const carrierWrites = Boolean(useFeatureFlag('dev-carrier-shipping'))
    const meta = data?.meta
    // Trang trên URL có thể vượt quá số trang (link cũ, vận đơn đổi nhóm):
    // về trang cuối còn dữ liệu, giữ nguyên bộ lọc — như màn Đơn hàng.
    useClampPage(
        page,
        meta?.total_pages,
        useCallback(
            (last: number) =>
                void navigate({
                    search: () => ({
                        ...FILTERS.toSearch(values),
                        page: last > 1 ? last : undefined,
                    }),
                    replace: true,
                }),
            [navigate, values],
        ),
    )
    useVtpShipmentListTracking({
        status: values.status,
        hasQuery: Boolean(values.q),
        // Trang cũ giữ tạm khi đổi lọc chưa phải kết quả của bộ lọc mới.
        state: isPlaceholderData ? 'pending' : status,
    })

    if (!hasPermission('sales_orders.read')) {
        return (
            <div className="p-8 text-center text-destructive">
                {m.common_permission_denied()}
            </div>
        )
    }

    const items = data?.data ?? []
    const count = (n?: number) => (n === undefined ? '—' : String(n))

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                icon={<PackageSearch className="size-5 shrink-0" />}
                title={m.vtp_shipments_title()}
                subtitle={m.vtp_shipments_subtitle()}
            />

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <KpiCard
                    title={m.vtp_shipments_status_in_delivery()}
                    value={count(summary?.in_delivery)}
                    icon={<Truck className="size-4" />}
                />
                <KpiCard
                    title={m.vtp_shipments_status_overdue()}
                    value={count(summary?.overdue)}
                    icon={<Clock className="size-4" />}
                />
                <KpiCard
                    title={m.vtp_shipments_status_returning()}
                    value={count(summary?.returning)}
                    icon={<Undo2 className="size-4" />}
                />
                <KpiCard
                    title={m.vtp_shipments_stat_delivered_month()}
                    value={count(summary?.delivered_this_month)}
                    icon={<CheckCircle2 className="size-4" />}
                />
            </div>

            <ListFilterBar
                {...barProps}
                onRefresh={() => void refetch()}
                refreshing={isFetching}
            />

            {isError ? (
                <div
                    role="alert"
                    className="flex items-center gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
                >
                    <span>{m.vtp_shipments_load_error()}</span>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void refetch()}
                    >
                        {m.carrier_tracking_retry()}
                    </Button>
                </div>
            ) : (
                <VtpShipmentsTable
                    items={items}
                    isLoading={isLoading}
                    canLink={
                        carrierWrites && hasPermission('carriers.dispatch')
                    }
                />
            )}

            {meta && meta.total_pages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                        {m.vtp_shipments_total_count({
                            count: String(meta.total),
                        })}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page === 1}
                            onClick={() => setPage(page - 1)}
                        >
                            {m.common_pagination_prev()}
                        </Button>
                        <span className="flex items-center px-2">
                            {page} / {meta.total_pages}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page >= meta.total_pages}
                            onClick={() => setPage(page + 1)}
                        >
                            {m.common_pagination_next()}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}
